import React, { useState, useEffect, useRef } from 'react'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string

export const DEFAULT_USA_POSITION: MapCoords = { lat: 37.0902, lng: -95.7315 }

export type MapCoords = {
    lat: number
    lng: number
}

export type MapCircle = {
    radius: number // in meters
    strokeColor?: string
    strokeWeight?: number
    fillOpacity?: number
}

export type MapProps = {
    zoom: number
    /** Sets the starting position on mount only. Not reactive. */
    initialPosition?: MapCoords
    /** Reactive center — pans the map whenever it changes. */
    center?: MapCoords
    circle?: MapCircle
    draggableMarker?: boolean
    options?: google.maps.MapOptions
    onChange?: (value: MapCoords, geo: { results: google.maps.GeocoderResult[] }) => void
    onMarkerDragEnd?: (value: MapCoords) => void
    onLoaded?: () => void
}

// singleton loader — setOptions must be called before any importLibrary
let apiConfigured = false
let mapsPromise: Promise<google.maps.MapsLibrary> | null = null

function ensureApiConfigured() {
    if (!apiConfigured) {
        setOptions({ key: GOOGLE_MAPS_API_KEY, libraries: ['places'] })
        apiConfigured = true
    }
}

function loadMaps(): Promise<google.maps.MapsLibrary> {
    ensureApiConfigured()
    if (!mapsPromise) {
        mapsPromise = importLibrary('maps')
    }
    return mapsPromise
}

const Map: React.FC<MapProps> = ({
    zoom,
    initialPosition,
    center,
    circle,
    draggableMarker = true,
    options,
    onChange,
    onMarkerDragEnd,
    onLoaded,
    ...otherProps
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<google.maps.Map | null>(null)
    const markerRef = useRef<google.maps.Marker | null>(null)
    const circleRef = useRef<google.maps.Circle | null>(null)
    const dragListenerRef = useRef<google.maps.MapsEventListener | null>(null)
    const onChangeRef = useRef(onChange)
    const onMarkerDragEndRef = useRef(onMarkerDragEnd)

    // capture initialPosition at mount time only
    const initialPositionRef = useRef(initialPosition || DEFAULT_USA_POSITION)

    const [isLoaded, setIsLoaded] = useState(false)

    // keep callback refs current
    useEffect(() => { onChangeRef.current = onChange }, [onChange])
    useEffect(() => { onMarkerDragEndRef.current = onMarkerDragEnd }, [onMarkerDragEnd])

    // initialize the map
    useEffect(() => {
        if (!containerRef.current) return undefined

        let cancelled = false

        ;(async () => {
            const [mapsLib, markerLib] = await Promise.all([
                loadMaps(),
                importLibrary('marker')
            ])

            if (cancelled || !containerRef.current) return

            const pos = center || initialPositionRef.current

            const map = new mapsLib.Map(containerRef.current, {
                zoom,
                center: pos,
                disableDefaultUI: true,
                ...options
            })

            mapRef.current = map

            const marker = new markerLib.Marker({
                map,
                position: pos,
                draggable: draggableMarker
            })
            markerRef.current = marker

            if (draggableMarker) {
                dragListenerRef.current = marker.addListener('dragend', async (e: google.maps.MapMouseEvent) => {
                    const latLng = e.latLng
                    if (!latLng) return

                    const coords: MapCoords = {
                        lat: latLng.lat(),
                        lng: latLng.lng()
                    }

                    // notify raw position (no geocode needed)
                    onMarkerDragEndRef.current?.(coords)

                    // reverse geocode if onChange is provided
                    if (onChangeRef.current) {
                        try {
                            const geocodingLib = await importLibrary('geocoding')
                            const geocoder = new geocodingLib.Geocoder()
                            const result = await geocoder.geocode({ location: latLng })
                            onChangeRef.current(coords, result)
                        } catch (err) {
                            console.warn('Map: reverse geocode failed', err)
                        }
                    }
                })
            }

            setIsLoaded(true)
            onLoaded?.()
        })()

        return () => {
            cancelled = true
            dragListenerRef.current?.remove()
            markerRef.current?.setMap(null)
            circleRef.current?.setMap(null)
            dragListenerRef.current = null
            markerRef.current = null
            circleRef.current = null
            mapRef.current = null
        }
    }, []) // mount once

    // sync reactive center prop
    useEffect(() => {
        if (!isLoaded || !mapRef.current || !center) return
        if (!isFinite(center.lat) || !isFinite(center.lng)) return

        const currentCenter = mapRef.current.getCenter()
        if (currentCenter &&
            Math.abs(currentCenter.lat() - center.lat) < 1e-5 &&
            Math.abs(currentCenter.lng() - center.lng) < 1e-5
        ) return

        mapRef.current.panTo(center)
        markerRef.current?.setPosition(center)
    }, [center?.lat, center?.lng, isLoaded])

    // sync zoom
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return undefined
        if (mapRef.current.getZoom() !== zoom) {
            mapRef.current.setZoom(zoom)
        }
    }, [zoom, isLoaded])

    // sync circle overlay
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return undefined

        // remove old circle
        circleRef.current?.setMap(null)
        circleRef.current = null

        if (!circle) return undefined

        const pos = center || markerRef.current?.getPosition()?.toJSON()
        if (!pos || !isFinite(pos.lat) || !isFinite(pos.lng)) return undefined

        circleRef.current = new google.maps.Circle({
            map: mapRef.current,
            center: pos,
            radius: circle.radius,
            strokeColor: circle.strokeColor || 'red',
            strokeWeight: circle.strokeWeight ?? 1,
            fillOpacity: circle.fillOpacity ?? 0,
            zIndex: 1
        })

        return () => {
            circleRef.current?.setMap(null)
            circleRef.current = null
        }
    }, [circle?.radius, circle?.strokeColor, circle?.strokeWeight, circle?.fillOpacity,
        center?.lat, center?.lng, isLoaded])

    return (
        <div
            ref={containerRef}
            style={{ width: '100%', height: '100%' }}
            {...otherProps}
        />
    )
}

export { Map }
